import {
  DateTime,
  Effect,
  Schema,
  SchemaTransformation,
  Equivalence as equivalence,
  Order as order,
} from 'effect';
import { dual } from 'effect/Function';

const isLeapYear = (year: number) => (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const monthLengths = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const daysInMonth = (year: number, month: number) =>
  month === 2 && isLeapYear(year) ? 29 : (monthLengths[month - 1] ?? 0);

const between = (minimum: number, maximum: number) =>
  Schema.Int.check(Schema.isBetween({ minimum, maximum }));

export class LocalDate extends Schema.Class<LocalDate>('@trm/datetime/LocalDate')(
  Schema.Struct({ year: between(1, 9999), month: between(1, 12), day: between(1, 31) }).check(
    Schema.makeFilter(({ year, month, day }) => day <= daysInMonth(year, month), {
      expected: 'a day that exists in its month',
    }),
  ),
) {}

const pad = (width: number) => (value: number) => String(value).padStart(width, '0');

export const formatIso = ({ year, month, day }: LocalDate) =>
  `${pad(4)(year)}-${pad(2)(month)}-${pad(2)(day)}`;

export const LocalDateFromIso = Schema.String.check(Schema.isPattern(/^\d{4}-\d{2}-\d{2}$/)).pipe(
  Schema.decodeTo(
    LocalDate,
    SchemaTransformation.transform({
      decode: (iso: string) => ({
        year: Number(iso.slice(0, 4)),
        month: Number(iso.slice(5, 7)),
        day: Number(iso.slice(8, 10)),
      }),
      encode: formatIso,
    }),
  ),
);

export const make = Schema.decodeUnknownOption(LocalDate);

export const fromIso = Schema.decodeUnknownOption(LocalDateFromIso);

export const fromDateTime = (self: DateTime.DateTime) => {
  const { year, month, day } = DateTime.toParts(self);
  return new LocalDate({ year, month, day });
};

const atUtcMidnight = ({ year, month, day }: LocalDate) =>
  DateTime.makeUnsafe({ year, month, day });

const fromUtc = (self: DateTime.Utc) => {
  const { year, month, day } = DateTime.toPartsUtc(self);
  return new LocalDate({ year, month, day });
};

export const startOfDay: {
  (zone: DateTime.TimeZone): (self: LocalDate) => DateTime.Zoned;
  (self: LocalDate, zone: DateTime.TimeZone): DateTime.Zoned;
} = dual(2, (self: LocalDate, zone: DateTime.TimeZone) =>
  DateTime.makeZonedUnsafe(
    { year: self.year, month: self.month, day: self.day },
    { timeZone: zone, adjustForTimeZone: true },
  ),
);

export const addDays: {
  (days: number): (self: LocalDate) => LocalDate;
  (self: LocalDate, days: number): LocalDate;
} = dual(2, (self: LocalDate, days: number) =>
  fromUtc(DateTime.add(atUtcMidnight(self), { days })),
);

const millisPerDay = 86_400_000;

export const daysBetween: {
  (other: LocalDate): (self: LocalDate) => number;
  (self: LocalDate, other: LocalDate): number;
} = dual(2, (self: LocalDate, other: LocalDate) =>
  Math.round(
    (DateTime.toEpochMillis(atUtcMidnight(other)) - DateTime.toEpochMillis(atUtcMidnight(self))) /
      millisPerDay,
  ),
);

const dayNumber = ({ year, month, day }: LocalDate) => year * 10_000 + month * 100 + day;

export const Order: order.Order<LocalDate> = order.mapInput(order.Number, dayNumber);

export const Equivalence: equivalence.Equivalence<LocalDate> = equivalence.mapInput(
  equivalence.Number,
  dayNumber,
);

export const today = (zone: DateTime.TimeZone) =>
  Effect.map(DateTime.now, (now) => fromDateTime(DateTime.setZone(now, zone)));
